import type { EmailOrigin } from '@fluenceur/types';
import { Span } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface EmailOriginCaptionProps {
  /** Where the resolved email came from. */
  origin: EmailOrigin;
  /** Whether an email is actually present to caption. */
  hasEmail: boolean;
}

/** Only non-manual, present emails get a provenance caption. */
const CAPTION_KEY: Partial<Record<NonNullable<EmailOrigin>, string>> = {
  affiliate: 'emailOrigin.affiliate',
  rights_acceptance: 'emailOrigin.rights_acceptance',
  enriched: 'emailOrigin.enriched',
};

/**
 * Discreet, muted caption naming where a contact's email came from. Rendered
 * only when the email was not typed by the brand (`affiliate` / `enriched`) and
 * an email is present; `manual` and no-email render nothing. Borders/contrast
 * only, no shadow (house rule).
 *
 * App-wide caption: reused by the contact rail, the discovery wishlist table,
 * and the outreach send panels, so it lives in `components/ui/` rather than any
 * single feature folder.
 */
const EmailOriginCaption: React.FC<EmailOriginCaptionProps> = ({ origin, hasEmail }) => {
  const { t } = useTranslation();
  const key = origin ? CAPTION_KEY[origin] : undefined;
  if (!hasEmail || !key) return null;

  return (
    <Span display="block" fontFamily="body" fontSize="11px" color="primary.400" lineHeight="1.4">
      {t(key)}
    </Span>
  );
};

export default EmailOriginCaption;
